import { useEffect, useState } from 'react'
import { api, type Clause, type FilterBrief } from './api'

/**
 * What a run of this filter will actually apply, in the order it applies it.
 *
 * The condition is the third of three things that decide who lands in the
 * list, and it was the only one any screen showed — a filter name, or a
 * dropdown of titles and a Run button. So the two things a condition never
 * mentions were invisible everywhere:
 * the suppression gates every scan starts from, and the filters above this one
 * that take an account before it is ever offered here. Reading G6's condition
 * on its own tells you the wrong set of people, because G5 sits above it and
 * both require an activated free account.
 *
 * Shown in three places, because it answers the same question at three
 * moments: before a scan (what am I about to select), after one (what
 * selected these people), and inside a campaign (who is this aimed at).
 *
 * Fetched separately from the filter list because it is per-filter and cheap
 * (no CRM connection, no scan — it describes rules, it does not evaluate
 * them), and because loading thirteen briefs to show one would be waste.
 */
export function FilterBriefPanel({ name, since }: { name: string; since?: string }) {
  const [brief, setBrief] = useState<FilterBrief | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setBrief(null)
    setError(null)
    api
      .filterBrief(name)
      .then((b) => live && setBrief(b))
      .catch((e) => live && setError(String((e as Error).message)))
    return () => {
      live = false
    }
  }, [name])

  if (error) return <div className="error">{error}</div>
  if (!brief) return <div className="loading">Reading the rules…</div>

  const { filter, gates, condition, coverage } = brief
  // A run records who matched at the time. The filter can be edited afterwards,
  // and then this description is of the current rule rather than the one that
  // selected these people — which is worth saying rather than quietly showing
  // the wrong thing.
  const editedSince = since ? new Date(filter.updated_at) > new Date(since) : false

  return (
    <div className="brief">
      {editedSince && (
        <div className="notice">
          This filter was edited on {new Date(filter.updated_at).toLocaleString()}, after
          this list was saved. What follows is the rule as it stands now, not necessarily
          the one that chose these members.
        </div>
      )}

      <BriefStep
        n={1}
        title="The mailable base"
        caption={
          'Every scan starts here, whichever filter is run. These gates live in the ' +
          'signal query itself, so an account failing one never reaches the condition ' +
          'below — no filter can widen past them.'
        }
      >
        <table className="tight">
          <tbody>
            {gates.map((g) => (
              <tr key={g.key}>
                <td className="mono">{g.key}</td>
                <td className="mono dim">{g.rule}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </BriefStep>

      <BriefStep
        n={2}
        title={
          condition.catch_all
            ? 'No condition — it matches everyone in the mailable base'
            : condition.decomposable
              ? `Its own condition — all ${condition.clauses.length} must hold`
              : 'Its own condition'
        }
        caption={
          condition.catch_all
            ? 'Countable, and refused as the audience of an enabled campaign: a campaign on ' +
              'it would write to everyone.'
            : condition.why
        }
      >
        <div className="mono cond">{filter.when ?? '(none)'}</div>
        {condition.clauses.map((c, i) => (
          <ClauseRow key={i} clause={c} />
        ))}
      </BriefStep>

      <BriefStep
        n={4}
        title={
          coverage.slots_with_own_copy === 0
            ? 'It has no approved copy of its own'
            : `Its copy — ${coverage.slots_with_own_copy} of ${coverage.slots_total} slots`
        }
        caption={
          'Composition never fails: a slot with no approved fragment naming this ' +
          'segment falls back to one that matches anybody. So a segment with no ' +
          'copy does not error — it sends sentences that would suit any reader, ' +
          'which is the failure that looks fine in a list of thirty.'
        }
      >
        <table className="tight">
          <tbody>
            {coverage.slots.map((slot) => {
              const own = coverage.own[slot]
              const has = own.approved > 0
              return (
                <tr key={slot}>
                  <td className="mono">{slot}</td>
                  <td>
                    {has ? (
                      <span className="badge ok">{own.approved} approved</span>
                    ) : (
                      <span className="badge parked">generic fallback</span>
                    )}
                  </td>
                  <td className="dim">
                    {own.draft + own.pending > 0
                      ? `${own.draft + own.pending} written, awaiting approval`
                      : ''}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </BriefStep>

      <div className="brief-foot">
        <strong>Not part of who is selected.</strong> Cooldown, lifetime caps and the message
        class live on each campaign that points at this filter, and apply at send — they
        change who receives an email, never who is in this run.
      </div>
    </div>
  )
}

function BriefStep({
  n,
  title,
  caption,
  children,
}: {
  n: number
  title: string
  caption?: string
  children?: React.ReactNode
}) {
  return (
    <div className="brief-step">
      <div className="brief-head">
        <span className="brief-n">{n}</span>
        <strong>{title}</strong>
      </div>
      {caption && <p className="hint brief-cap">{caption}</p>}
      {children}
    </div>
  )
}

/**
 * One requirement, and where the words in it come from.
 *
 * A band name is not self-explanatory: `traffic_band == dead` only means
 * something once "dead" is "< 200 visits in 30 days". The rule and the source
 * column are short enough to show; the glossary's note is often a paragraph
 * of hard-won reasoning, so it sits behind a toggle rather than burying the
 * condition it is annotating.
 */
function ClauseRow({ clause }: { clause: Clause }) {
  const [open, setOpen] = useState(false)
  const rules = clause.rules ?? []
  return (
    <div className="clause">
      <div className="clause-line">
        <span className="mono clause-name">{clause.name ?? clause.raw}</span>
        <span className="clause-test">{clause.test}</span>
        {clause.values.map((v) => (
          <span key={v} className="mono clause-val">
            {v}
          </span>
        ))}
        {clause.kind !== 'unknown' && <span className="badge catchall">{clause.kind}</span>}
        {clause.note && (
          <button className="linkish" onClick={() => setOpen(!open)}>
            {open ? 'hide why' : 'why'}
          </button>
        )}
      </div>
      {rules.map((r) => (
        <div key={r.band} className="clause-rule">
          <span className="mono">{r.band}</span> = <span className="mono">{r.rule}</span>
          {clause.unit && <span className="dim"> ({clause.unit})</span>}
        </div>
      ))}
      {/* The bound excludes NULL in both directions, so the population it
          leaves out is worth naming on the screen that explains who a filter
          selects — it is the difference between "customised recently" and
          "customised, and recently". */}
      {clause.kind === 'number' && clause.null_means && (
        <div className="clause-rule" style={{ color: 'var(--warn)' }}>
          Excludes “{clause.null_means.trim()}”
        </div>
      )}
      {clause.kind === 'band' && clause.all_bands && (
        <div className="clause-rule dim">
          bands: <span className="mono">{clause.all_bands.join(', ')}</span>
        </div>
      )}
      {clause.source && (
        <div className="clause-rule dim">
          from <span className="mono">{clause.source}</span>
        </div>
      )}
      {clause.kind === 'unknown' && (
        <div className="clause-rule dim">
          Not in variables.yaml or flags.yaml — nothing here can say what it means.
        </div>
      )}
      {open && clause.note && <pre className="notes">{clause.note.trim()}</pre>}
    </div>
  )
}
